import * as React from 'react';

/**
 * Divider orientation
 */
export type DividerOrientation = 'horizontal' | 'vertical';

/**
 * Label position options
 */
export type DividerLabelPosition = 'start' | 'center' | 'end';

/**
 * Spacing value type - maps to margin scale
 * 0 = 0, 1 = 4px, 2 = 8px, 3 = 12px, 4 = 16px, 5 = 20px, 6 = 24px, 8 = 32px
 */
export type DividerSpacing = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8;

/**
 * Divider color intensity
 */
export type DividerColor = 'default' | 'light' | 'dark';

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Orientation of the divider
   * @default 'horizontal'
   */
  orientation?: DividerOrientation;
  /**
   * Optional label text to display on the divider
   */
  label?: string;
  /**
   * Position of the label
   * @default 'center'
   */
  labelPosition?: DividerLabelPosition;
  /**
   * Spacing around the divider (maps to margin)
   * For horizontal: vertical margin, for vertical: horizontal margin
   * @default 4 (16px)
   */
  spacing?: DividerSpacing;
  /**
   * Color intensity of the divider line
   * @default 'default'
   */
  color?: DividerColor;
}

/**
 * Spacing classes for horizontal dividers (margin-top and margin-bottom)
 */
const horizontalSpacingClasses: Record<DividerSpacing, string> = {
  0: '',
  1: 'my-1',
  2: 'my-2',
  3: 'my-3',
  4: 'my-4',
  5: 'my-5',
  6: 'my-6',
  8: 'my-8',
};

/**
 * Spacing classes for vertical dividers (margin-left and margin-right)
 */
const verticalSpacingClasses: Record<DividerSpacing, string> = {
  0: '',
  1: 'mx-1',
  2: 'mx-2',
  3: 'mx-3',
  4: 'mx-4',
  5: 'mx-5',
  6: 'mx-6',
  8: 'mx-8',
};

/**
 * Color classes for horizontal rule element (using border color)
 */
const hrColorClasses: Record<DividerColor, string> = {
  light: 'border-secondary-100',
  default: 'border-secondary-200',
  dark: 'border-secondary-300',
};

/**
 * Divider component
 *
 * A visual separator for content sections. Supports horizontal and vertical
 * orientations, optional labels, and customizable spacing and colors.
 *
 * @example
 * // Basic horizontal divider
 * <Divider />
 *
 * @example
 * // Vertical divider in a flex container
 * <div className="flex h-8 items-center">
 *   <span>Left</span>
 *   <Divider orientation="vertical" spacing={4} />
 *   <span>Right</span>
 * </div>
 *
 * @example
 * // Divider with label
 * <Divider label="or" />
 *
 * @example
 * // Divider with label at start position
 * <Divider label="Section" labelPosition="start" />
 *
 * @example
 * // Divider with custom spacing and color
 * <Divider spacing={8} color="dark" />
 */
const Divider = React.forwardRef<HTMLDivElement, DividerProps>(
  (
    {
      orientation = 'horizontal',
      label,
      labelPosition = 'center',
      spacing = 4,
      color = 'default',
      className = '',
      ...props
    },
    ref
  ) => {
    // Horizontal divider without label - use semantic <hr>
    if (orientation === 'horizontal' && !label) {
      const combinedClasses = [
        'w-full border-0 border-t',
        hrColorClasses[color],
        horizontalSpacingClasses[spacing],
        className,
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <hr
          ref={ref as React.Ref<HTMLHRElement>}
          className={combinedClasses}
          aria-orientation="horizontal"
          {...(props as React.HTMLAttributes<HTMLHRElement>)}
        />
      );
    }

    // Horizontal divider with label
    if (orientation === 'horizontal' && label) {
      const containerClasses = [
        'flex items-center w-full',
        horizontalSpacingClasses[spacing],
        className,
      ]
        .filter(Boolean)
        .join(' ');

      const lineClasses = ['flex-1 border-0 border-t', hrColorClasses[color]].join(' ');

      const labelClasses = 'text-xs text-secondary-500 px-4 whitespace-nowrap';

      return (
        <div
          ref={ref}
          className={containerClasses}
          {...props}
        >
          {labelPosition === 'start' && (
            <>
              <span className={labelClasses}>{label}</span>
              <hr className={lineClasses} aria-orientation="horizontal" />
            </>
          )}
          {labelPosition === 'center' && (
            <>
              <hr className={lineClasses} aria-orientation="horizontal" />
              <span className={labelClasses}>{label}</span>
              <hr className={lineClasses} aria-hidden="true" />
            </>
          )}
          {labelPosition === 'end' && (
            <>
              <hr className={lineClasses} aria-orientation="horizontal" />
              <span className={labelClasses}>{label}</span>
            </>
          )}
        </div>
      );
    }

    // Vertical divider (cannot have labels in vertical orientation) - use semantic <hr>
    const verticalClasses = [
      'w-px self-stretch inline-block border-0 border-l',
      hrColorClasses[color],
      verticalSpacingClasses[spacing],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <hr
        ref={ref as React.Ref<HTMLHRElement>}
        className={verticalClasses}
        aria-orientation="vertical"
        {...(props as React.HTMLAttributes<HTMLHRElement>)}
      />
    );
  }
);

Divider.displayName = 'Divider';

export { Divider };
